import { useState } from 'react';
import { walletApi, CreateBankAccountPayload } from '@/api/wallet-api';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/Button';
import { Trash2, Plus, AlertCircle, Building2, Star } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks';

const ACCOUNT_TYPES = [
  { value: 'CHECKING', label: 'Conta Corrente' },
  { value: 'SAVINGS', label: 'Poupança' },
] as const;

const BRAZILIAN_BANKS = [
  { code: '001', name: 'Banco do Brasil' },
  { code: '033', name: 'Banco Santander' },
  { code: '104', name: 'Caixa Econômica Federal' },
  { code: '237', name: 'Bradesco' },
  { code: '341', name: 'Itaú' },
  { code: '077', name: 'Banco Inter' },
  { code: '745', name: 'Citibank' },
  { code: '748', name: 'Banco Sicredi' },
];

export function BankAccountsPage() {
  const location = useLocation();
  const { user } = useAuth();
  const backPath = location.pathname.startsWith('/medico') || user?.role === 'DOCTOR' ? '/medico/carteira' : '/carteira';

  const queryClient = useQueryClient();

  const { data: bankAccounts, isLoading } = useQuery({
    queryKey: ['bankAccounts'],
    queryFn: () => walletApi.getBankAccounts(),
  });

  const createMutation = useMutation({
    mutationFn: (payload: CreateBankAccountPayload) => walletApi.createBankAccount(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bankAccounts'] });
      setShowForm(false);
      resetForm();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => walletApi.deleteBankAccount(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bankAccounts'] });
    },
  });

  const setDefaultMutation = useMutation({
    mutationFn: (id: string) => walletApi.setDefaultBankAccount(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bankAccounts'] });
    },
  });

  const [showForm, setShowForm] = useState(false);
  const [bankCode, setBankCode] = useState('');
  const [bankName, setBankName] = useState('');
  const [agency, setAgency] = useState('');
  const [account, setAccount] = useState('');
  const [accountType, setAccountType] = useState<'CHECKING' | 'SAVINGS'>('CHECKING');
  const [holderName, setHolderName] = useState('');
  const [holderCpf, setHolderCpf] = useState('');
  const [isDefault, setIsDefault] = useState(false);

  const resetForm = () => {
    setBankCode('');
    setBankName('');
    setAgency('');
    setAccount('');
    setAccountType('CHECKING');
    setHolderName('');
    setHolderCpf('');
    setIsDefault(false);
  };

  const handleBankChange = (code: string) => {
    setBankCode(code);
    const bank = BRAZILIAN_BANKS.find(b => b.code === code);
    if (bank) setBankName(bank.name);
  };

  const formatCpf = (value: string) => {
    const digits = value.replace(/\D/g, '');
    if (digits.length <= 11) {
      return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    }
    return value;
  };

  const handleSubmit = () => {
    if (!bankCode || !agency || !account || !holderName || !holderCpf) return;
    createMutation.mutate({
      bankCode,
      bankName,
      agency,
      account,
      accountType,
      holderName,
      holderCpf: holderCpf.replace(/\D/g, ''),
      isDefault,
    });
  };

  if (isLoading) {
    return (
      <div className="max-w-2xl mx-auto space-y-4">
        <div className="h-8 w-48 animate-pulse bg-muted rounded" />
        <div className="h-48 animate-pulse bg-muted rounded-xl" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div>
        <Link
          to={backPath}
          className="text-sm text-primary-600 hover:text-primary-700 font-medium mb-2 inline-block"
        >
          &larr; Voltar para Carteira
        </Link>
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Building2 className="w-6 h-6" />
            Contas Bancárias
          </h1>
          {!showForm && (
            <Button onClick={() => setShowForm(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Adicionar Conta
            </Button>
          )}
        </div>
      </div>

      {bankAccounts && bankAccounts.length > 0 && (
        <div className="space-y-4">
          {bankAccounts.map(ba => (
            <div key={ba.id} className="bg-white dark:bg-gray-800 rounded-xl border border-border shadow-sm overflow-hidden">
              <div className="p-6 space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <Building2 className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="font-semibold">{ba.bankName}</p>
                      <p className="text-sm text-muted-foreground">Banco {ba.bankCode}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {ba.isDefault && (
                      <span className="text-xs px-2 py-1 rounded-full bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300 flex items-center gap-1">
                        <Star className="w-3 h-3" />
                        Principal
                      </span>
                    )}
                    {ba.isVerified && (
                      <span className="text-xs px-2 py-1 rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300">
                        Verificada
                      </span>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">Agência</p>
                    <p className="font-medium font-mono">{ba.maskedAgency}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Conta</p>
                    <p className="font-medium font-mono">{ba.maskedAccount}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Tipo</p>
                    <p className="font-medium">{ACCOUNT_TYPES.find(t => t.value === ba.accountType)?.label}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">CPF Titular</p>
                    <p className="font-medium font-mono">{ba.maskedHolderCpf}</p>
                  </div>
                </div>

                <div className="flex gap-2 pt-2 border-t">
                  {!ba.isDefault && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setDefaultMutation.mutate(ba.id)}
                      disabled={setDefaultMutation.isPending}
                    >
                      <Star className="w-4 h-4 mr-2" />
                      Definir como Principal
                    </Button>
                  )}
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => deleteMutation.mutate(ba.id)}
                    disabled={deleteMutation.isPending}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {(!bankAccounts || bankAccounts.length === 0) && !showForm && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-border shadow-sm">
          <div className="py-12 text-center">
            <Building2 className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">Nenhuma conta bancária cadastrada</h3>
            <p className="text-sm text-muted-foreground mb-6">
              Cadastre uma conta bancária para receber transferências e saques.
            </p>
            <Button onClick={() => setShowForm(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Cadastrar Conta
            </Button>
          </div>
        </div>
      )}

      {showForm && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-border shadow-sm overflow-hidden">
          <div className="p-6 border-b border-border">
            <h2 className="font-semibold">Cadastrar Conta Bancária</h2>
          </div>
          <div className="p-6 space-y-4">
            {createMutation.error && (
              <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-lg text-sm">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                <span>Erro ao salvar conta bancária. Verifique os dados e tente novamente.</span>
              </div>
            )}

            <div className="space-y-2">
              <label className="text-sm font-medium">Banco</label>
              <select
                value={bankCode}
                onChange={(e) => handleBankChange(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              >
                <option value="">Selecione o banco</option>
                {BRAZILIAN_BANKS.map(b => (
                  <option key={b.code} value={b.code}>{b.name} ({b.code})</option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Agência</label>
                <input
                  type="text"
                  value={agency}
                  onChange={(e) => setAgency(e.target.value.replace(/\D/g, ''))}
                  placeholder="0000"
                  maxLength={5}
                  className="w-full px-3 py-2 rounded-lg border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Tipo de Conta</label>
                <select
                  value={accountType}
                  onChange={(e) => setAccountType(e.target.value as 'CHECKING' | 'SAVINGS')}
                  className="w-full px-3 py-2 rounded-lg border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                >
                  {ACCOUNT_TYPES.map(t => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Número da Conta</label>
              <input
                type="text"
                value={account}
                onChange={(e) => setAccount(e.target.value.replace(/\D/g, ''))}
                placeholder="00000-0"
                className="w-full px-3 py-2 rounded-lg border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Nome do Titular</label>
              <input
                type="text"
                value={holderName}
                onChange={(e) => setHolderName(e.target.value)}
                placeholder="Nome completo conforme documento"
                className="w-full px-3 py-2 rounded-lg border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">CPF do Titular</label>
              <input
                type="text"
                value={holderCpf}
                onChange={(e) => setHolderCpf(formatCpf(e.target.value))}
                placeholder="000.000.000-00"
                maxLength={14}
                className="w-full px-3 py-2 rounded-lg border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="isDefault"
                checked={isDefault}
                onChange={(e) => setIsDefault(e.target.checked)}
                className="w-4 h-4 rounded border-input"
              />
              <label htmlFor="isDefault" className="text-sm">Definir como conta principal</label>
            </div>

            <div className="flex gap-2 pt-2">
              <Button
                onClick={handleSubmit}
                disabled={createMutation.isPending || !bankCode || !agency || !account || !holderName || !holderCpf}
              >
                {createMutation.isPending ? 'Salvando...' : 'Salvar'}
              </Button>
              <Button
                variant="outline"
                onClick={() => { setShowForm(false); resetForm(); }}
              >
                Cancelar
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
